import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Snackbar,
	Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { Alert } from "@mui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import type { Drawing } from "../../../../models/WorkOrderDrawing";
import { AssetDrawingsTable } from "./asset-drawings-table";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import {
	linkWorkOrderDrawings,
	unLinkWorkOrderDrawings,
} from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";

interface Props {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	WorkOrderId: number | null;
	WorkOrderName: string;
	Title: string;
	AssetName: string;
}

export const AssetDrawingsPicker: React.FC<Props> = ({
	show,
	onHide,
	WorkOrderId,
	WorkOrderName,
	Title,
	AssetName,
}) => {
	const {
		state: { workOrderDrawingsList },
	} = useWorkOrderModalsContext();

	const [selectedDrawings, setSelectedDrawings] = useState<Drawing[]>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");

	useEffect(() => {
		if (show) {
			setSelectedDrawings(
				workOrderDrawingsList.filter((drawing) => drawing.IsAssetResource),
			);
		}
	}, [show, workOrderDrawingsList]);

	const handleSelectionChanged = (newSelectedDrawings: Drawing[]) => {
		setSelectedDrawings(newSelectedDrawings);
	};

	const getNewlyAddedDrawings = (
		currentSelected: Drawing[],
		originalList: Drawing[],
	): Drawing[] => {
		return currentSelected.filter(
			(selectedDrawing) =>
				!originalList.some(
					(existingDrawing) =>
						existingDrawing.DocumentName === selectedDrawing.DocumentName &&
						existingDrawing.IsAssetResource,
				),
		);
	};

	const getRemovedDrawings = (
		currentSelected: Drawing[],
		originalList: Drawing[],
	): Drawing[] => {
		return originalList.filter(
			(originalDrawing) =>
				originalDrawing.IsAssetResource &&
				!currentSelected.some(
					(selectedDrawing) =>
						selectedDrawing.DocumentName === originalDrawing.DocumentName,
				),
		);
	};

	const handleSave = async () => {
		const newlyAddedDrawings = getNewlyAddedDrawings(
			selectedDrawings,
			workOrderDrawingsList,
		);
		const removedDrawings = getRemovedDrawings(
			selectedDrawings,
			workOrderDrawingsList,
		);

		if (newlyAddedDrawings.length > 0) {
			const newAssetDrawings = newlyAddedDrawings.map((drawing) => ({
				DocumentName: drawing.DocumentName,
				IsAssetResource: true,
			}));
			await linkWorkOrderDrawings(
				WorkOrderId as number,
				newAssetDrawings as any,
			);
			onHide(true);
		} else onHide(false);

		if (removedDrawings.length > 0) {
			const assetDrawingsToRemoved = removedDrawings.map((drawing) => ({
				DocumentName: drawing.DocumentName,
				IsAssetResource: true,
			}));
			await unLinkWorkOrderDrawings(
				WorkOrderId as number,
				assetDrawingsToRemoved as any,
			);
			onHide(true);
		} else onHide(false);
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						ASSET RESOURCES
					</Typography>
					<Typography
						style={{
							display: "flex",
							alignItems: "center",
							fontSize: "1.3rem",
							flexWrap: "wrap",
							gap: "8px",
							padding: "8px 0 0",
						}}
					>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Work Order:</span>
							<span style={{ marginLeft: "4px" }}>{WorkOrderName}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Title:</span>
							<span style={{ marginLeft: "4px" }}>{Title}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Asset:</span>
							<span style={{ marginLeft: "4px" }}>{AssetName}</span>
						</div>
					</Typography>
				</DialogTitle>
				<DialogContent>
					<AssetDrawingsTable
						workOrderDrawings={workOrderDrawingsList.filter(
							(drawing) => drawing.IsAssetResource,
						)}
						onSelectionChanged={handleSelectionChanged}
					/>
				</DialogContent>
				<DialogActions
					sx={{ padding: "16px 24px 24px", justifyContent: "flex-end", gap: 2 }}
				>
					<Button
						variant="contained"
						color="inherit"
						onClick={() => onHide(false)}
						sx={{ minWidth: 120 }}
					>
						CANCEL
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSave}
						sx={{ minWidth: 120 }}
					>
						SAVE
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
